import React from 'react';
import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {GeneralInfoGETResponse, GeneralInfoPATCHRequest, Recruitment, GeneralPanelState} from "../../../../utils/types";
import {AppThunk} from "../../../../app/store";
import {getGeneralInfo, patchGeneralInfo} from "../../../../app/protected_api";
import {FormInstance} from "antd/lib/form";
import {Store} from "rc-field-form/lib/interface";

export const messageDuration = 3;
export const initialState: GeneralPanelState = {
    generalInfo: {
        preferred_first_name: '',
        preferred_last_name: '',
        preferred_email: '',
        preferred_pronouns: '',
        primary_campus: '',
        show_preferred_email: false,
        show_preferred_pronouns: false,
        show_primary_campus: false,
        languages: [],
        show_languages: false,
        interests: '',
        sfu_link: '',
        description: '',
        show_description: false,
        C3I: false,
        recruitment: {
            accepting_graduate_student: false,
            note: '',
            years: [],
        }
    },
    loading: false,
    showSuccess: false,
    showFailure: false,
    error: null,
}

const generalPanelSlice = createSlice({
    name: 'generalPanel',
    initialState,
    reducers: {
        setAcceptingGraduateStudent: (state, action: PayloadAction<boolean>) => {
            state.generalInfo.recruitment.accepting_graduate_student = action.payload;
        },
        setShowPreferredEmail: (state, action: PayloadAction<boolean>) => {
            state.generalInfo.show_preferred_email = action.payload;
        },
        setShowPreferredPronouns: (state, action: PayloadAction<boolean>) => {
            state.generalInfo.show_preferred_pronouns = action.payload;
        },
        setShowPrimaryCampus: (state, action: PayloadAction<boolean>) => {
            state.generalInfo.show_primary_campus = action.payload;
        },
        setShowLanguages: (state, action: PayloadAction<boolean>) => {
            state.generalInfo.show_languages = action.payload;
        },
        setShowDescription: (state, action: PayloadAction<boolean>) => {
            state.generalInfo.show_description = action.payload;
        },
        startLoadingGeneralInfo: (state) => {
            state.loading = true;
        },
        generalInfoLoadedSuccess: (state, action: PayloadAction<GeneralInfoGETResponse>) => {
            state.loading = false;
            state.generalInfo = action.payload;
        },
        generalInfoLoadedFailure: (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.error = action.payload;
        },
        setShowSuccess: (state, action: PayloadAction<boolean>) => {
            state.showSuccess = action.payload;
        },
        setShowFailure: (state, action: PayloadAction<boolean>) => {
            state.showFailure = action.payload;
        },
    },
});

export const {
    setShowPreferredEmail,
    setShowPreferredPronouns,
    setShowPrimaryCampus,
    setShowLanguages,
    setShowDescription,
    setAcceptingGraduateStudent,
    startLoadingGeneralInfo,
    generalInfoLoadedFailure,
    generalInfoLoadedSuccess,
    setShowSuccess,
    setShowFailure,
} = generalPanelSlice.actions;

export default generalPanelSlice;

export const loadGeneralInfo = (
    form: FormInstance, 
    setEditorValue: React.Dispatch<React.SetStateAction<string>>, 
    setInputLengths: React.Dispatch<any>
): AppThunk => async dispatch => {
    try {
        dispatch(startLoadingGeneralInfo());
        const generalInfo = await getGeneralInfo();

        form.setFieldsValue({
            preferred_first_name: generalInfo.preferred_first_name,
            preferred_last_name: generalInfo.preferred_last_name,
            preferred_email: generalInfo.preferred_email,
            preferred_pronouns: generalInfo.preferred_pronouns,
            primary_campus: generalInfo.primary_campus,
            show_preferred_email: generalInfo.show_preferred_email,
            show_preferred_pronouns: generalInfo.show_preferred_pronouns,
            show_primary_campus: generalInfo.show_primary_campus,
            accepting_graduate_student: generalInfo.recruitment.accepting_graduate_student,
            note: generalInfo.recruitment.note,
            years: generalInfo.recruitment.years,
            interests: generalInfo.interests,
            sfu_link: generalInfo.sfu_link,
            // "description" form uses custom TinymceEditor component, so we update value with setEditorValue()
            // description: generalInfo.description,
            show_description: generalInfo.show_description,
            languages: generalInfo.languages === null ? []: generalInfo.languages,
            show_languages: generalInfo.show_languages,
            C3I: generalInfo.C3I,
        })
        setEditorValue(generalInfo.description);
        setInputLengths({
            preferred_first_name: generalInfo.preferred_first_name?.length,
            preferred_last_name: generalInfo.preferred_last_name?.length,
            preferred_email: generalInfo.preferred_email?.length,
            preferred_pronouns: generalInfo.preferred_pronouns?.length,
            primary_campus: generalInfo.primary_campus?.length,
            note: generalInfo.recruitment.note?.length,
            interests: generalInfo.interests?.length,
            sfu_link: generalInfo.sfu_link?.length
        });
        dispatch(generalInfoLoadedSuccess(generalInfo));
    } catch (err) {
        dispatch(generalInfoLoadedFailure(err.stack));
    }
};

export const submitGeneralInfo = (
    values: Store, 
    callback?: ()=>void
): AppThunk => async (dispatch, getState) => {

    const recruitment: Recruitment = {
        accepting_graduate_student: values.accepting_graduate_student,
        note: values.note,
        years: values.years
    }

    const body: GeneralInfoPATCHRequest = {
        preferred_first_name: values.preferred_first_name,
        preferred_last_name: values.preferred_last_name,
        preferred_email: values.preferred_email,
        preferred_pronouns: values.preferred_pronouns,
        primary_campus: values.primary_campus,
        show_preferred_email: values.show_preferred_email,
        show_preferred_pronouns: values.show_preferred_pronouns,
        show_primary_campus: values.show_primary_campus,
        recruitment: recruitment,
        interests: values.interests,
        sfu_link: values.sfu_link,
        languages: values.languages,
        show_languages: values.show_languages,
        description: values.description,
        show_description: values.show_description,
        C3I: values.C3I,
    };

    try {
        dispatch(startLoadingGeneralInfo());
        const generalInfo = await patchGeneralInfo(body);
        dispatch(generalInfoLoadedSuccess(generalInfo));
        dispatch(showSuccessMessage());
        if (callback) callback();
    } catch (err) {
        dispatch(generalInfoLoadedFailure(err.stack));
        dispatch(showFailureMessage());
    }
};

export const showSuccessMessage = (): AppThunk => (dispatch, getState) => {
    dispatch(setShowSuccess(true));
    setTimeout(() => {
        dispatch(setShowSuccess(false));
    }, messageDuration * 1000);
};

export const showFailureMessage = (): AppThunk => (dispatch, getState) => {
    dispatch(setShowFailure(true));
    setTimeout(() => {
        dispatch(setShowFailure(false));
    }, messageDuration * 1000);
};